import React, { useEffect, useMemo, useState } from 'react';

export type AdminToastType = 'success' | 'error';

export type AdminToastPayload = {
    type: AdminToastType;
    text: string;
    /** optional stable id to prevent duplicates */
    id?: string;
    /** ms */
    duration?: number;
};

const EVENT_NAME = 'netsight-admin-toast';

export function showAdminToast(payload: AdminToastPayload) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: payload }));
}

type ToastItem = Required<Pick<AdminToastPayload, 'type' | 'text'>> & {
    id: string;
    expiresAt: number;
};

function Icon({ type }: { type: AdminToastType }) {
    return type === 'success' ? (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
    ) : (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
    );
}

export function AdminToastHost() {
    const [items, setItems] = useState<ToastItem[]>([]);

    const now = Date.now();
    const activeItems = useMemo(() => items.filter((t) => t.expiresAt > now), [items, now]);

    useEffect(() => {
        const onToast = (e: Event) => {
            const ce = e as CustomEvent<AdminToastPayload>;
            const detail = ce.detail;
            if (!detail?.text) return;

            const id = detail.id ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
            const duration = Math.max(1200, Math.min(8000, detail.duration ?? 2600));

            setItems((prev) => {
                // de-dupe by id if provided
                if (prev.some((p) => p.id === id)) return prev;
                return [
                    ...prev,
                    {
                        id,
                        type: detail.type,
                        text: detail.text,
                        expiresAt: Date.now() + duration,
                    },
                ];
            });
        };

        window.addEventListener(EVENT_NAME, onToast as EventListener);
        return () => window.removeEventListener(EVENT_NAME, onToast as EventListener);
    }, []);

    useEffect(() => {
        if (activeItems.length === 0) return;
        const t = window.setTimeout(() => {
            setItems((prev) => prev.filter((x) => x.expiresAt > Date.now()));
        }, 250);
        return () => window.clearTimeout(t);
    }, [activeItems.length]);

    if (activeItems.length === 0) return null;

    return (
        <div className="pointer-events-none fixed bottom-8 right-8 z-[1000] flex flex-col gap-2">
            {activeItems.slice(-3).map((t) => (
                <div
                    key={t.id}
                    className="pointer-events-auto animate-in fade-in slide-in-from-right-4 duration-300"
                >
                    <div
                        className={`flex min-w-[280px] items-center gap-3 rounded-xl border px-4 py-3 shadow-2xl ${
                            t.type === 'success'
                                ? 'border-green-500/30 bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100'
                                : 'border-red-500/30 bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100'
                        }`}
                        role="status"
                        aria-live="polite"
                    >
                        <div
                            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                                t.type === 'success'
                                    ? 'bg-green-500/10 text-green-500'
                                    : 'bg-red-500/10 text-red-500'
                            }`}
                        >
                            <Icon type={t.type} />
                        </div>

                        <div className="flex-grow">
                            <p className="text-[13px] font-semibold">{t.text}</p>
                        </div>

                        <button
                            type="button"
                            onClick={() => setItems((prev) => prev.filter((x) => x.id !== t.id))}
                            className="rounded-md p-1 transition-colors hover:bg-gray-100 dark:hover:bg-white/10"
                            aria-label="Close"
                        >
                            <svg
                                className="h-4 w-4 text-gray-500 dark:text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

